import { HomePageComponentNodeName } from '@ps/isp-web-services-types';
import React from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { WidgetFC } from '@swf/core/types/Widget';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';

import styles from './SportsHomePageWidget.scss';
import { HomePageLoadingState } from './components/LoadingState/LoadingState';
import { sportsHomePageWidgetModel, useSportsHomePageWidget } from './model';
import { COMPONENT_MAP, FALLBACK_COMPONENT_ORDER } from './model/config';

const isValidComponentKey = (key: string): key is HomePageComponentNodeName =>
  Object.values(HomePageComponentNodeName).includes(key as HomePageComponentNodeName);

const WidgetContent = () => {
  const { widgetSelect, widgetDispatch } = useSportsHomePageWidget();
  let componentOrder = useSelector(widgetSelect.components);

  if (!componentOrder.length) {
    componentOrder = FALLBACK_COMPONENT_ORDER;
  }

  return (
    <div className={styles.wrapper}>
      {componentOrder.filter(isValidComponentKey).map((componentName) => {
        const Component = COMPONENT_MAP[componentName];
        return Component ? (
          <Component
            key={componentName}
            widgetSelect={widgetSelect}
            widgetDispatch={widgetDispatch}
          />
        ) : null;
      })}
    </div>
  );
};

const SportsHomePageWidget: WidgetFC = () => {
  const { widgetSelect, widgetDispatch } = useSportsHomePageWidget();
  const isLoading = useSelector(widgetSelect.isLoading);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const hasErrored = useSelector(widgetSelect.hasErrored);
  const marketIds = useSelector(widgetSelect.marketIds);

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (isLoading && !hasPreloaded) {
    return <HomePageLoadingState />;
  }

  if (hasErrored) {
    return null;
  }

  return <WidgetContent />;
};

SportsHomePageWidget.model = sportsHomePageWidgetModel;

export default withWidgetTestId(SportsHomePageWidget, 'sports-home-page');
